import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, KeyRound, Mail, RotateCcw } from "lucide-react";
import AuthService from "../../services/authService";
import Button from "../../shared/components/Button";

// Cognito password policy (matches the pool): 8+ chars, upper, lower, number, symbol.
const PASSWORD_POLICY =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

type Step = "request" | "code" | "password";

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("request");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username) return;
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await AuthService.forgotPassword(username.trim());
      setInfo("We've sent a verification code to your registered email.");
      setStep("code");
    } catch (err) {
      setError(typeof err === "string" ? err : "Could not send reset code.");
    } finally {
      setLoading(false);
    }
  };

  // Cognito has no "verify code only" API — the code is checked by
  // ConfirmForgotPassword together with the new password. This step therefore
  // validates the format and advances; a wrong code surfaces on submit and
  // sends the user back here with the error.
  const continueToPassword = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setError(null);
    setInfo(null);
    setStep("password");
  };

  const resetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!newPassword) return;
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!PASSWORD_POLICY.test(newPassword)) {
      setError(
        "Password must be at least 8 characters and include upper- and lower-case letters, a number, and a symbol.",
      );
      return;
    }
    setLoading(true);
    try {
      await AuthService.confirmPassword(
        username.trim(),
        code.trim(),
        newPassword,
      );
      navigate("/login", {
        state: { message: "Password reset. Please sign in." },
      });
    } catch (err) {
      const message =
        typeof err === "string" ? err : "Could not reset password.";
      // An invalid/expired code is a code problem, not a password problem —
      // send the user back to the code step rather than leaving them stuck.
      if (/code/i.test(message)) {
        setStep("code");
        setError(`${message} Please re-enter the code or request a new one.`);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await AuthService.forgotPassword(username.trim());
      setInfo("A new verification code has been sent.");
    } catch (err) {
      setError(typeof err === "string" ? err : "Could not resend code.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400";

  const STEP_LABEL: Record<Step, string> = {
    request: "Step 1 of 3 · Your email",
    code: "Step 2 of 3 · Verification code",
    password: "Step 3 of 3 · New password",
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-100 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10">
        <h2 className="text-2xl font-bold text-blue-700 mb-1 text-center">
          Reset your password
        </h2>
        <p className="text-xs text-gray-400 mb-5 text-center uppercase tracking-wider">
          {STEP_LABEL[step]}
        </p>

        {info && (
          <div className="bg-green-100 text-green-700 rounded p-2 mb-4 text-center text-sm">
            {info}
          </div>
        )}
        {error && (
          <div className="bg-red-100 text-red-700 rounded p-2 mb-4 text-center text-sm">
            {error}
          </div>
        )}

        {step === "request" && (
          <>
            <p className="text-gray-600 mb-4 text-center text-sm">
              Enter your email and we'll send you a verification code.
            </p>
            <form onSubmit={requestCode} className="space-y-4">
              <input
                type="text"
                placeholder="Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                className={inputClass}
              />
              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={loading}
                icon={Mail}
              >
                {loading ? "Sending..." : "Send reset code"}
              </Button>
            </form>
          </>
        )}

        {step === "code" && (
          <>
            <p className="text-gray-600 mb-4 text-center text-sm">
              Enter the code sent to{" "}
              <span className="font-medium">{username}</span>.
            </p>
            <form onSubmit={continueToPassword} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                placeholder="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                required
                autoFocus
                className={`${inputClass} text-center text-2xl tracking-widest`}
              />
              <Button
                type="submit"
                size="lg"
                fullWidth
                icon={ArrowRight}
                iconPosition="right"
              >
                Continue
              </Button>
            </form>
            <div className="mt-3 text-center">
              <Button
                variant="ghost"
                size="sm"
                icon={RotateCcw}
                onClick={resendCode}
                loading={loading}
              >
                Resend code
              </Button>
            </div>
          </>
        )}

        {step === "password" && (
          <>
            <p className="text-gray-600 mb-4 text-center text-sm">
              Choose a new password for{" "}
              <span className="font-medium">{username}</span>.
            </p>
            <form onSubmit={resetPassword} className="space-y-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
                className={inputClass}
              />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className={inputClass}
              />
              <div className="flex items-center">
                <input
                  id="show-reset-password"
                  type="checkbox"
                  checked={showPassword}
                  onChange={() => setShowPassword((v) => !v)}
                  className="mr-2"
                />
                <label
                  htmlFor="show-reset-password"
                  className="text-sm text-gray-600"
                >
                  Show Password
                </label>
              </div>
              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={loading}
                icon={KeyRound}
              >
                {loading ? "Resetting..." : "Reset password"}
              </Button>
            </form>
            <div className="mt-3 text-center">
              <Button
                variant="ghost"
                size="sm"
                icon={ArrowLeft}
                onClick={() => {
                  setError(null);
                  setStep("code");
                }}
              >
                Back to code
              </Button>
            </div>
          </>
        )}

        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/login")}
            className="text-blue-600"
          >
            Back to sign in
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
